"use client"

import type { ReactNode } from "react"
import Image from "next/image"
import * as Dialog from "@radix-ui/react-dialog"
import {
  X,
  Globe,
  Gauge,
  Sparkles,
  SlidersHorizontal,
  Check,
  Ban,
  Wifi,
  Phone,
  RefreshCw,
} from "lucide-react"
import { cn } from "@/lib/util"
import { useTranslation } from "@/i18n/client"
import { formatQuota, getFlagUrl, hasSpeedRule, type PlanExhaust } from "./helpers"
import type { RecommendationPlan } from "./types"

interface PlanDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: RecommendationPlan
  iso2s: string[]
  exhaust: PlanExhaust
  /** Pre-formatted price, e.g. "$5.18". */
  priceLabel: string
  buying?: boolean
  onBuy: () => void
  lng?: string
}

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <section className="py-4">
      <h3 className="mb-2.5 flex items-center gap-2 text-[13px] font-bold text-stone-900">
        <span className="text-stone-400">{icon}</span>
        {title}
      </h3>
      {children}
    </section>
  )
}

function SpecRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <dt className="text-[13px] text-stone-500">{label}</dt>
      <dd className="text-right text-[13px] font-semibold text-stone-900">{value}</dd>
    </div>
  )
}

export default function PlanDetailModal({
  open,
  onOpenChange,
  plan,
  iso2s,
  exhaust,
  priceLabel,
  buying,
  onBuy,
  lng,
}: PlanDetailModalProps) {
  const { t } = useTranslation(lng, "product")

  const quotaStr = formatQuota(plan.data.quota, t("plan-quota-unlimited"))
  const quotaLabel =
    plan.data.daily && plan.data.quota !== -1
      ? t("plan-card-daily-data", { data: quotaStr })
      : quotaStr
  const dayLabel =
    plan.data.day === 1
      ? t("plan-card-days-count-singular", { count: plan.data.day })
      : t("plan-card-days-count", { count: plan.data.day })

  // Network coverage — one row per covered country, with operators + speed badges.
  const countryCodes =
    plan.data.support_countries && plan.data.support_countries.length > 0
      ? plan.data.support_countries
      : iso2s
  const networkRows = countryCodes.map((iso) => {
    const code = iso.toUpperCase()
    const ops = plan.data.network[code] || plan.data.network[iso] || []
    const speedSet = new Set<string>()
    for (const op of ops) {
      for (const s of op.speed) {
        if (s.includes("5G")) speedSet.add("5G")
        else if (s.includes("4G")) speedSet.add("4G")
        else if (s.includes("3G")) speedSet.add("3G")
      }
    }
    return {
      iso: code,
      operators: ops.map((o) => o.operator),
      speeds: ["5G", "4G", "3G"].filter((s) => speedSet.has(s)),
    }
  })

  const speedBadgeClass: Record<string, string> = {
    "5G": "bg-emerald-50 text-emerald-700",
    "4G": "bg-sky-50 text-sky-700",
    "3G": "bg-stone-100 text-stone-500",
  }

  const features = [
    plan.tags?.hotspot
      ? { key: "hotspot", icon: <Wifi className="h-3.5 w-3.5" />, label: t("plan-detail-feature-hotspot") }
      : null,
    plan.tags?.phoneNumber
      ? { key: "phone", icon: <Phone className="h-3.5 w-3.5" />, label: t("plan-detail-feature-phone") }
      : null,
    plan.tags?.topup
      ? { key: "topup", icon: <RefreshCw className="h-3.5 w-3.5" />, label: t("plan-detail-feature-topup") }
      : null,
  ].filter(Boolean) as { key: string; icon: ReactNode; label: string }[]

  const showSpeed = hasSpeedRule(exhaust)
  const isThrottle = exhaust.mode === "throttle"
  const afterValue = isThrottle
    ? t("plan-detail-after-throttle", { speed: exhaust.throttleLabel ?? "" })
    : t("plan-detail-after-terminate")

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-stone-900/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <Dialog.Content
          onClick={(e) => e.stopPropagation()}
          className="fixed left-1/2 top-1/2 z-[70] flex max-h-[85vh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl bg-white shadow-2xl duration-200 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]"
        >
          {/* Header */}
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-stone-100 px-5 py-4">
            <div>
              <Dialog.Title className="text-[16px] font-bold text-stone-900">
                {t("plan-detail-title")}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[12px] text-stone-400">
                {quotaLabel} · {dayLabel}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label={t("plan-detail-close")}
              className="cursor-pointer rounded-full p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/50"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {/* Scrollable body */}
          <div className="divide-y divide-stone-100 overflow-y-auto px-5">
            {/* Network coverage */}
            <Section icon={<Globe className="h-4 w-4" />} title={t("plan-detail-section-network")}>
              <ul className="space-y-2">
                {networkRows.map((row) => (
                  <li key={row.iso} className="flex items-center gap-2">
                    <Image
                      src={getFlagUrl(row.iso)}
                      alt=""
                      width={18}
                      height={13}
                      className="shrink-0 rounded-[1px] object-cover"
                    />
                    <span className="min-w-0 flex-1 text-[12px] leading-relaxed text-stone-600">
                      {row.operators.length > 0 ? row.operators.join(" · ") : row.iso}
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      {row.speeds.map((s) => (
                        <span
                          key={s}
                          className={cn(
                            "rounded px-1 text-[9px] font-bold leading-relaxed",
                            speedBadgeClass[s]
                          )}
                        >
                          {s}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* Speed / throttle rule — only when known from description */}
            {showSpeed && (
              <Section icon={<Gauge className="h-4 w-4" />} title={t("plan-detail-section-speed")}>
                <dl>
                  <SpecRow
                    label={t("plan-detail-after-label")}
                    value={
                      <span className={isThrottle ? "text-stone-900" : "text-rose-600"}>
                        {afterValue}
                      </span>
                    }
                  />
                  <SpecRow
                    label={t("plan-detail-cutoff-label")}
                    value={
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                          isThrottle ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"
                        )}
                      >
                        {isThrottle ? <Check className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
                        {isThrottle ? t("plan-detail-cutoff-no") : t("plan-detail-cutoff-yes")}
                      </span>
                    }
                  />
                </dl>
              </Section>
            )}

            {/* Features */}
            {features.length > 0 && (
              <Section icon={<Sparkles className="h-4 w-4" />} title={t("plan-detail-section-features")}>
                <ul className="flex flex-wrap gap-2">
                  {features.map((f) => (
                    <li
                      key={f.key}
                      className="inline-flex items-center gap-1.5 rounded-full bg-stone-50 px-3 py-1.5 text-[12px] font-medium text-stone-700"
                    >
                      <span className="text-emerald-600">{f.icon}</span>
                      {f.label}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Specifications */}
            <Section
              icon={<SlidersHorizontal className="h-4 w-4" />}
              title={t("plan-detail-section-specs")}
            >
              <dl>
                <SpecRow label={t("plan-detail-spec-data")} value={quotaLabel} />
                <SpecRow label={t("plan-detail-spec-duration")} value={dayLabel} />
              </dl>
            </Section>
          </div>

          {/* Footer CTA */}
          <div className="shrink-0 border-t border-stone-100 px-5 py-4">
            <button
              type="button"
              disabled={buying}
              onClick={onBuy}
              className="w-full cursor-pointer rounded-2xl bg-home-orange_1 py-3.5 text-[15px] font-bold text-white shadow-sm transition-colors duration-200 hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/50 disabled:cursor-wait disabled:opacity-70"
            >
              {buying ? t("filter-loading") : `${t("plan-detail-buy")} · ${priceLabel}`}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
